import React, { useEffect, useMemo, useState } from 'react'
import { Link as GatsbyLink, navigate } from 'gatsby'
import clsx from 'clsx'
import Layout from 'components/layouts/tow-column'
import {
  Button,
  Card,
  Col,
  Divider,
  Error,
  ErrorAlert,
  Link,
  Paragraph,
  Row,
  Text,
  Title,
} from 'components/common'
import {
  AddressEditorModal,
  CustomerBookingsTable,
  CustomerMemo,
  OverviewEditorModal,
} from 'components/customers'
import { OrderApproval, useGeneralSettingQuery } from 'lib/graphql/storeadmin'
import { useCustomer } from 'lib/hooks'
import strFormatter from 'lib/str-formatter'
import { ellipsisText } from 'utils/custom-functions'
import RemainingDiscountCard from 'components/bookings/cards/RemainingDiscountCard'

export interface DetailProps {
  id: string
  type: 'personal' | 'corporate'
}

const onBack = () => navigate('../')

export const Detail = ({ id, type }: DetailProps): JSX.Element => {
  const { data: generalSettingData, loading: generalSettingLoading } =
    useGeneralSettingQuery()
  const [showOverviewModal, setShowOverviewModal] = useState(false)
  const [showAddressModal, setShowAddressModal] = useState(false)
  const [showErrorAlert, setErrorAlert] = useState(false)
  const [customerErrorMessages, setCustomerErrorMessages] = useState([])

  const openOverviewModal = (): void => setShowOverviewModal(true)
  const closeOverviewModalOnSuccess = (): void => {
    setShowOverviewModal(false)
    setErrorAlert(false)
  }
  const openAddressModal = (): void => setShowAddressModal(true)
  const closeAddressModal = (): void => {
    setShowAddressModal(false)
  }
  const closeAddressModalOnSuccess = (): void => {
    setShowAddressModal(false)
    setErrorAlert(false)
  }
  const openErrorAlert = (): void => {
    setErrorAlert(true)
    setShowOverviewModal(false)
    setShowAddressModal(false)
  }
  const closeErrorAlert = (): void => setErrorAlert(false)
  const customersModalErrorMessages = (messages: string[]): void =>
    setCustomerErrorMessages(messages)

  const {
    customer,
    futureOrders,
    allOrders,
    query,
    loading: initialLoading,
    error,
    variables,
    refetch,
  } = useCustomer(id, {})
  const [tabLoading, setTabLoading] = useState(false)
  useEffect(() => setTabLoading(initialLoading), [initialLoading])

  const title = useMemo(() => {
    switch (type) {
      case 'corporate':
        return ellipsisText(customer?.corporationName, 12)?.text
      case 'personal':
      default:
        return ellipsisText(`${customer?.lastName} ${customer?.firstName}`, 12)
          ?.text
    }
  }, [type, customer])

  const onTabChange = async (
    key: 'all' | 'pending' | 'past'
  ): Promise<void> => {
    setTabLoading(true)
    try {
      switch (key) {
        case 'pending':
          await refetch({
            ...variables,
            past: null,
            approval: OrderApproval.Pending,
          })
          break
        case 'past':
          await refetch({
            ...variables,
            past: variables.future,
            approval: null,
          })
          break
        case 'all':
        default:
          await refetch({ ...variables, past: null, approval: null })
          break
      }
    } finally {
      setTabLoading(false)
    }
  }

  if (error) {
    return Error.render(error)
  }

  return (
    <>
      <Layout
        key={id}
        title={title}
        loading={initialLoading}
        actions={<GatsbyLink to={`./edit`}>編集</GatsbyLink>}
        onBack={onBack}
      >
        <Layout.Section size="large">
          <Layout.Item>
            {showErrorAlert && (
              <ErrorAlert
                onCloseAlert={closeErrorAlert}
                alertDescription={customerErrorMessages}
              />
            )}
            <Card shadow>
              <Title level={5}>次の利用予定</Title>
              <Paragraph mute>
                利用予定日が今日より未来のものが表示されます。
              </Paragraph>
              {/* Reservation Table */}
              <CustomerBookingsTable
                dataSource={futureOrders}
                loading={initialLoading}
              />
            </Card>
          </Layout.Item>
          <Layout.Item>
            <Card
              title={<Title level={5}>予約一覧</Title>}
              tabList={[
                {
                  key: 'all',
                  tab: 'すべて',
                },
                { key: 'pending', tab: '承認待ち' },
                { key: 'past', tab: '過去予約分' },
              ]}
              shadow
              onTabChange={onTabChange}
            >
              {/* all Reservation Table */}
              <CustomerBookingsTable
                dataSource={allOrders}
                loading={tabLoading}
              />
            </Card>
          </Layout.Item>
        </Layout.Section>

        <Layout.Section size="small">
          <Layout.Item>
            <Card loading={initialLoading} shadow>
              {/* お客様の概要 section*/}
              <div>
                {/* Customer email OverView and Modal */}
                <Row>
                  <Col span={20}>
                    <Title level={5}>お客様の概要</Title>
                  </Col>

                  <Col
                    span={4}
                    className={clsx('flex', 'flex-end', 'justify-end')}
                  >
                    <Button
                      type={'link'}
                      style={{ padding: 0, top: 0 }}
                      onClick={openOverviewModal}
                    >
                      編集
                    </Button>
                  </Col>
                </Row>

                <Row>
                  <Col span={24}>
                    {customer?.email && (
                      <Link href={`mailto:${customer?.email}`} copyable>
                        {customer?.email}
                      </Link>
                    )}
                  </Col>

                  <Col span={24}>
                    {customer?.phone && (
                      <Link
                        href={
                          customer?.phone &&
                          strFormatter.phoneURI(customer.phone)
                        }
                        copyable
                      >
                        {customer?.phone && strFormatter.phone(customer.phone)}
                      </Link>
                    )}
                  </Col>
                </Row>
              </div>

              <Divider />

              {/* 住所 section */}
              <div>
                <Row>
                  <Col span={20}>
                    <Title level={5}>住所</Title>
                  </Col>
                  <Col
                    span={4}
                    className={clsx('flex', 'flex-end', 'justify-end')}
                  >
                    <Button
                      type={'link'}
                      style={{ padding: 0 }}
                      onClick={openAddressModal}
                    >
                      編集
                    </Button>
                  </Col>
                </Row>

                <Col span={24}>
                  <Text>
                    〒
                    {customer?.address?.zip &&
                      strFormatter.zip(customer.address.zip)}
                  </Text>
                </Col>
                <Col span={24}>
                  <Text>
                    {customer?.address?.prefecture} {customer?.address?.city}
                  </Text>
                </Col>
                <Col span={24}>
                  <Text>
                    {customer?.address?.address1} {customer?.address?.address2}
                  </Text>
                </Col>
              </div>

              {/* 担当者 section */}
              {type === 'corporate' && (
                <>
                  <div>
                    <Divider />
                    <Title level={5}>担当者</Title>
                    <Col span={24}>
                      <Text>{`${customer?.lastName} ${customer?.firstName}`}</Text>
                    </Col>
                  </div>
                </>
              )}

              {/* 予約可能フラグ section */}
              <>
                <div>
                  <Divider />
                  <Title level={5}>予約可能フラグ</Title>
                  <Col span={24}>
                    <Text>
                      {customer?.bookableFlag ? '予約可能' : '予約不可'}
                    </Text>
                  </Col>
                </div>
              </>

              {!generalSettingLoading &&
                generalSettingData?.generalSetting.domain === 'saga-arena' &&
                type === 'corporate' && (
                  <>
                    {/* サブアリーナ予約可能フラグの設定 */}
                    <>
                      <div>
                        <Divider />
                        <Title level={5}>サブアリーナ予約可能フラグ</Title>
                        <Col span={24}>
                          <Text>
                            {customer?.metaFields?.edges?.some(
                              ({ node }) => node.key === 'subarena-permitted'
                            )
                              ? '予約可能'
                              : '予約不可'}
                          </Text>
                        </Col>
                      </div>
                    </>

                    {/* 県内団体フラグの設定 */}
                    <>
                      <div>
                        <Divider />
                        <Title level={5}>県内団体フラグ</Title>
                        <Col span={24}>
                          <Text>
                            {customer?.metaFields?.edges?.some(
                              ({ node }) => node.key === 'local-organization'
                            )
                              ? '県内団体'
                              : '県外団体'}
                          </Text>
                        </Col>
                      </div>
                    </>
                  </>
                )}
            </Card>
          </Layout.Item>

          {/* 顧客メモ */}
          <Layout.Item>
            <CustomerMemo memo={customer?.note} loading={initialLoading} />
          </Layout.Item>

          {customer?.type === 'CORPORATE' && (
            <Layout.Item>
              <Card>
                <RemainingDiscountCard id={customer?.id} />
              </Card>
            </Layout.Item>
          )}

          {/* Tag Components */}
          {/* <CustomersTags /> */}
        </Layout.Section>
      </Layout>

      <OverviewEditorModal
        type={type}
        defaultValues={{
          id,
          corporationName: customer?.corporationName,
          corporationNameKana: customer?.corporationNameKana,
          firstName: customer?.firstName,
          lastName: customer?.lastName,
          email: customer?.email,
          phone: customer?.phone,
        }}
        refetchQueries={[{ query, variables }]}
        visible={showOverviewModal}
        onSuccess={closeOverviewModalOnSuccess}
        onCancel={() => setShowOverviewModal(false)}
        onErrorMessage={customersModalErrorMessages}
        onErrorAlert={openErrorAlert}
      />

      <AddressEditorModal
        type={type}
        defaultValues={{
          id,
          address1: customer?.address?.address1,
          address2: customer?.address?.address2,
          city: customer?.address?.city,
          prefectureCode: customer?.address?.prefectureCode,
          zip: customer?.address?.zip,
        }}
        refetchQueries={[{ query, variables }]}
        visible={showAddressModal}
        onSuccess={closeAddressModalOnSuccess}
        onCancel={closeAddressModal}
        onErrorMessage={customersModalErrorMessages}
        onErrorAlert={openErrorAlert}
      />
    </>
  )
}
